import { type ReactNode } from 'react'
import { Navigate, Route, Routes, useParams } from 'react-router-dom'
import { RequireAuth } from 'grabbitbot-legacy-ui'
import { Shell } from '../layout/Shell'

import { LoginPage } from '../pages/auth/LoginPage'
import { RegisterPage } from '../pages/auth/RegisterPage'
import { LogoutPage } from '../pages/auth/LogoutPage'
import { WalletListPage } from '../pages/wallets/WalletListPage'
import { WalletSheetPage } from '../pages/wallets/WalletSheetPage'
import { SwapPage } from '../pages/swap/SwapPage'
import { OpenOrdersPage } from '../pages/orders/OpenOrdersPage'
import { OrderHistoryPage } from '../pages/orders/OrderHistoryPage'
import { ServiceAccountsPage } from '../pages/account/ServiceAccountsPage'
import { UserPage } from '../pages/account/UserPage'

// Every app route except login/register/logout sits behind `RequireAuth`
// (the legacy Angular app had no route guards at all — audit §10).
function Protected({ children }: { children: ReactNode }) {
  return (
    <RequireAuth fallback={<Navigate to="/login" replace />} pending={<p>Loading…</p>}>
      <Shell>{children}</Shell>
    </RequireAuth>
  )
}

/** Legacy deep links were `/:tradingType/wallets[/:coin]` (always `spot`) —
 * kept working, redirected to the shorter routes. */
function LegacyWalletCoinRedirect() {
  const { coin = '' } = useParams()
  return <Navigate to={`/wallets/${coin}`} replace />
}

/**
 * Route inventory — ported from the legacy Angular router (audit
 * `docs/audit/components/2026-09-28-simple.wnfexchange.com.md` §4) as decided in
 * the port plan §8: KEEP login/logout/user, wallets, wallet sheet, service
 * accounts (+ NEW open orders and order history). NOT ported: dashboard,
 * status, payment/Stripe, admin, strategies, and the 13 unrouted components —
 * that is the GrabbitBot bot-V2 / billing surface, dropped for WnF Exchange
 * (K3: no external or paying users). The e-mail flows (`register-validate`,
 * `password-forgot*`, `passwordsetup-validate`) are replaced by Auth0.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/logout" element={<LogoutPage />} />

      <Route path="/wallets" element={<Protected><WalletListPage /></Protected>} />
      <Route path="/wallets/:coin" element={<Protected><WalletSheetPage /></Protected>} />
      <Route path="/swap" element={<Protected><SwapPage /></Protected>} />
      <Route path="/orders/open" element={<Protected><OpenOrdersPage /></Protected>} />
      <Route path="/orders/history" element={<Protected><OrderHistoryPage /></Protected>} />
      <Route path="/orders/history/:coin1/:coin2" element={<Protected><OrderHistoryPage /></Protected>} />
      <Route path="/service-accounts" element={<Protected><ServiceAccountsPage /></Protected>} />
      <Route path="/user" element={<Protected><UserPage /></Protected>} />

      <Route path="/:tradingType/wallets" element={<Navigate to="/wallets" replace />} />
      <Route path="/:tradingType/wallets/:coin" element={<LegacyWalletCoinRedirect />} />

      <Route path="/" element={<Navigate to="/wallets" replace />} />
      <Route path="*" element={<Navigate to="/wallets" replace />} />
    </Routes>
  )
}
